import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Switch, Button ,DatePicker, InputNumber} from "antd";
import TextArea from "antd/es/input/TextArea";
import { doc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "@/firebaseConfig.js";
import {getNames} from "country-list";
import dayjs from "dayjs";

const AppFormModal = ({ open, onCancel , app, users, setSuccess, onSuccess }) => {
    const [form] = Form.useForm();
    const [ratingEnabled, setRatingEnabled] = useState(false);
    const [likesEnabled, setLikesEnabled] = useState(false);
    const [reviewsEnabled, setReviewsEnabled] = useState(false);
    const [loading, setLoading] = useState(false);

    const countryNames = getNames();
    const ratingOptions = [1, 2, 3, 4, 5];
    const searchOptions = [
        "1 day",
        "2, 3 day",
        "2, 3, 8, 16 day (recommended)",
        "2, 3, 8, 16, 23 day",
    ];

    useEffect(() => {
        if (app) {

            const statsList = app.stats
                ? Object.entries(app.stats).map(([date, values]) => ({
                    date: dayjs(date),
                    clicks: values.clicks,
                    sales: values.sales,
                }))
                : [];


            form.setFieldsValue({
                ...app,
                ownerEmail: app.ownerEmail,
                ownerId: app.ownerId,
                stats: statsList,
                available: app.available ?? false,
                roas: app.roas ?? "",
                accountHealth: app.accountHealth ?? "",
            });

            setRatingEnabled(!!app.rating);
            setLikesEnabled(!!app.likes);
            setReviewsEnabled(!!app.reviews);
        } else {
            form.resetFields();
            setRatingEnabled(false);
            setLikesEnabled(false);
            setReviewsEnabled(false);
        }
    }, [app, form]);



    const onFinish = async (values) => {
        setLoading(true);
        try {
            const selectedUser = users.find((u) => u.email.toLowerCase() === values.ownerEmail.toLowerCase());

            const statsObj = (values.stats || []).reduce((acc, item) => {
                if (item.date) {
                    const dateStr = item.date.format("YYYY-MM-DD");
                    acc[dateStr] = {
                        clicks: item.clicks || 0,
                        sales: item.sales || 0,
                    };
                }
                return acc;
            }, {});

            const payload = {
                appName: values.appName,
                appUrl: values.appUrl,
                orderId: values.orderId,
                geotargeting: values.geotargeting,
                search: values.search,
                rating: ratingEnabled ? values.rating : null,
                likes: likesEnabled ? values.likes : null,
                reviews: reviewsEnabled ? values.reviews : null,
                ownerId: selectedUser?.uid,
                ownerEmail: values.ownerEmail,
                status: values.status || "moderation",
                stats: statsObj || null,
                available: values.available ?? null,
                roas: values.roas ?? null,
                accountHealth: values.accountHealth ?? null,
            };

            if (!app) {
                payload.createdAt = new Date();
            }

            const docRef = doc(db, "apps", app?.id || crypto.randomUUID());
            if (app) {
                await updateDoc(docRef, payload);
            } else {
                await setDoc(docRef, payload);
            }
            setSuccess(true);
            onSuccess?.();
        } catch (err) {
            console.error("Error saving app:", err);
        } finally {
            setLoading(false); // Сбрасываем состояние загрузки в любом случае
        }
    };


    return (
        <Modal
            open={open}
            onCancel={onCancel}
            title={app ? "Edit Application" : "Create Application"}
            footer={null}
            destroyOnClose
        >
            <Form layout="vertical" form={form} onFinish={onFinish}>

                <Form.Item
                    label="User Email"
                    name="ownerEmail"
                    rules={[{ required: true, message: "Select user email" }]}
                >
                    <Select
                        showSearch
                        placeholder="Select user"
                        optionFilterProp="children"
                    >
                        {users.map((u) => (
                            <Select.Option key={u.uid} value={u.email}>
                                {u.email}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>



                <Form.Item
                    label="Application Name"
                    name="appName"
                    rules={[{ required: true, message: "Please enter app name" }]}
                >
                    <Input.TextArea rows={4} />
                </Form.Item>
                <Form.Item
                    name="available"
                    label="Available"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
                <Form.Item
                    name="roas"
                    label="ROAS"
                    rules={[{ required: true, message: 'Please enter ROAS' }]}
                >
                    <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                    name="accountHealth"
                    label="Account Health"
                    rules={[{ required: true, message: 'Please select account health' }]}
                >
                    <Select placeholder="Select account health">
                        <Option value="Excellent">Excellent</Option>
                        <Option value="Good">Good</Option>
                        <Option value="Average">Average</Option>
                        <Option value="Poor">Poor</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Status"
                    name="status"
                    rules={[{ required: true, message: "Please select status" }]}
                >
                    <Select placeholder="Select status">
                        <Select.Option value="moderation">Moderation</Select.Option>
                        <Select.Option value="approved">Approved</Select.Option>
                        <Select.Option value="rejected">Rejected</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Package Name / URL"
                    name="appUrl"
                    rules={[{ required: true, message: "Please enter app URL" }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    label="Order ID"
                    name="orderId"
                    rules={[{ required: true, message: "Please enter order ID" }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    label="Geo Targeting"
                    name="geotargeting"
                    rules={[{ required: true, message: "Select a country" }]}
                >
                    <Select  showSearch placeholder="Select a country">
                        {countryNames.map((country) => (
                            <Select.Option key={country} value={country}>
                                {country}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item label="Rating">
                    <Switch
                        checked={ratingEnabled}
                        onChange={setRatingEnabled}
                        checkedChildren="Enabled"
                        unCheckedChildren="Disabled"
                    />
                    {ratingEnabled && (
                        <Form.Item
                            name="rating"
                            rules={[{ required: true, message: "Select a rating" }]}
                        >
                            <Select style={{ marginTop: "10px" }} placeholder="Select rating">
                                {ratingOptions.map((r) => (
                                    <Select.Option key={r} value={r}>
                                        {r}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                </Form.Item>

                <Form.Item label="Likes">
                    <Switch
                        checked={likesEnabled}
                        onChange={setLikesEnabled}
                        checkedChildren="Enabled"
                        unCheckedChildren="Disabled"
                    />
                    {likesEnabled && (
                        <Form.Item
                            name="likes"
                            rules={[{ required: true, message: "Select number of likes" }]}
                        >
                            <Select style={{ marginTop: "10px" }} placeholder="Select likes">
                                {ratingOptions.map((r) => (
                                    <Select.Option key={r} value={r}>
                                        {r}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                </Form.Item>

                <Form.Item label="Reviews">
                    <Switch
                        checked={reviewsEnabled}
                        onChange={setReviewsEnabled}
                        checkedChildren="Enabled"
                        unCheckedChildren="Disabled"
                    />
                    {reviewsEnabled && (
                        <Form.Item
                            name="reviews"
                            rules={[{ required: true, message: "Enter reviews" }]}
                        >
                            <TextArea style={{ marginTop: "10px" }} rows={3} />
                        </Form.Item>
                    )}
                </Form.Item>

                <Form.Item
                    label="Prohibition of deletion with restart"
                    name="search"
                    rules={[{ required: true, message: "Select an option" }]}
                >
                    <Select placeholder="Select an option">
                        {searchOptions.map((opt) => (
                            <Select.Option key={opt} value={opt}>
                                {opt}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>


                <Form.List name="stats">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => (
                                <div key={key} style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
                                    <Form.Item
                                        {...restField}
                                        name={[name, "date"]}
                                        label="Date"
                                        rules={[{ required: true, message: "Choose a date" }]}
                                    >
                                        <DatePicker format="YYYY-MM-DD" />
                                    </Form.Item>

                                    <Form.Item
                                        {...restField}
                                        name={[name, "clicks"]}
                                        label="Clicks"
                                        rules={[{ required: true, message: "Clicks" }]}
                                    >
                                        <InputNumber min={0} />
                                    </Form.Item>

                                    <Form.Item
                                        {...restField}
                                        name={[name, "sales"]}
                                        label="Sales"
                                        rules={[{ required: true, message: "Sales" }]}
                                    >
                                        <InputNumber min={0} />
                                    </Form.Item>

                                    <Button danger onClick={() => remove(name)}>
                                        Delete
                                    </Button>
                                </div>
                            ))}

                            <Button type="dashed" onClick={() => add()} style={{ width: "100%", marginBottom: 10 }}>
                                Add statistics
                            </Button>
                        </>
                    )}
                </Form.List>


                <Form.Item>
                    <Button loading={loading} type="primary" htmlType="submit">
                        {app ? "Update Application" : "Create Application"}
                    </Button>

                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AppFormModal;

